import type { ReactElement } from "react";
import { Link, Outlet } from "react-router-dom";

import { CompareBar } from "../components/CompareBar";
import { useAuth } from "../contexts/AuthContext";
import { useTenant } from "../contexts/TenantContext";

/**
 * Base layout с header + footer.
 *
 * Header adapts к session state:
 * - unauthenticated → showing "Войти" link
 * - authenticated → showing "Каталог" + identity + logout
 * - admin → extra "Admin" link
 */
export function Layout(): ReactElement {
  const { session, hasAdminJwt, logout } = useAuth();
  const tenant = useTenant();
  const identity =
    session?.client_name ?? session?.email ?? session?.user_identity ?? null;
  // Cycle 22 STORY-U3 — tenant attribution из session (server-side bound)
  // ИЛИ из URL context (subdomain/path) если ещё не logged in.
  const activeTenantSlug = session?.tenant_slug ?? tenant.slug;

  return (
    <div className="app-shell">
      <header className="app-header">
        <div className="app-header__container">
          <Link to="/" className="app-header__brand">
            <span className="app-header__logo" aria-hidden="true">
              ⬡
            </span>
            <span className="app-header__title">
              IT-Navigate{" "}
              <span className="app-header__subtitle">Client Portal</span>
            </span>
          </Link>
          {activeTenantSlug && (
            <span
              className="app-header__tenant"
              title={`Tenant: ${activeTenantSlug} (source: ${tenant.source})`}
              style={{
                marginLeft: "16px",
                padding: "4px 12px",
                background: "#e6f0ff",
                color: "#0b5fff",
                borderRadius: "12px",
                fontSize: "12px",
                fontWeight: 600,
                whiteSpace: "nowrap",
              }}
            >
              🏢 {activeTenantSlug}
            </span>
          )}
          <nav className="app-header__nav" aria-label="Main">
            {session?.authenticated ? (
              <>
                <Link to="/catalog" className="app-header__link">
                  Каталог
                </Link>
                <span className="app-header__identity" title={identity ?? ""}>
                  {identity}
                </span>
                <button
                  className="btn-secondary btn-small"
                  onClick={() => logout().then(() => (window.location.href = "/"))}
                >
                  Выйти
                </button>
              </>
            ) : (
              <Link to="/login" className="app-header__link">
                Войти
              </Link>
            )}
            {hasAdminJwt && (
              <Link to="/admin" className="app-header__link app-header__link--admin">
                Admin
              </Link>
            )}
          </nav>
        </div>
      </header>

      <main className="app-main">
        <Outlet />
      </main>

      {/* Cycle 9 STORY-802: Floating compare bar (visible when products selected) */}
      <CompareBar />

      <footer className="app-footer">
        <div className="app-footer__container">
          <span>© IT-Navigate, 2026</span>
          <span className="app-footer__version">v0.1.0 MVP</span>
        </div>
      </footer>
    </div>
  );
}
